import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import axios from "axios";
import { _config } from '../Constants';
import { createConversationDoc, getFirestoreDocId, getCurrentUserId, getLearningActivityDoc, getUserMemory, getCurrentUserName, getCurrentOrganizationId, getCurrentOrganizationName, getIdTokenOrNull } from '../utilities/firebase';
import { auth } from '../utilities/firebaseApp';
import { getUrlParameter } from '../utilities/urlUtils.js';
import { getTargetLanguageText } from '../utilities/targetLanguageTranslations';
import { getActivityDuration } from '../utilities/activities.ts'; // Adjust the path as needed
import { getMainStartActivity, getFreeformStartActivity } from '../utilities/activitySelection';
import { useLiveActivityProgress } from '../hooks/useLiveActivityProgress';
import { shouldShowSessionProgress } from '../utilities/activityTracking';
import { getConversationProgressIndex, isLearningActivity, mergeConversationProgress } from '../utilities/conversationProgress';
import { stripSignals, isCompletionResponse } from '../utilities/signals';
import { classifyRecordedBlob, RECORDING_GUARD_FALLBACK_MESSAGES } from '../utilities/recordingGuards';
import { streamTTSAudio, stopStreamingPlayback, isTTSStreamingEnabled } from '../utilities/ttsStream';
import { streamConversationTurn, isTurnStreamingEnabled, isTurnStreamEligible, blobToBase64, TurnStreamError } from '../utilities/turnStream';
import { postUploadWithRetry } from '../utilities/uploadRetry';

import { useTranslation } from 'react-i18next';
import { useSharedState } from '../hooks/SharedStateContext';
import { usePlanner } from '../hooks/PlannerContext';
import { useEndConversation } from '../hooks/endConversation';
import { useTimer } from '../hooks/TimerContext';
import { useWindowSize } from 'react-use';

import ConversationComponent from './ConversationComponent'; // Adjust the path as needed
import ConversationProgressSummary from './ConversationProgressSummary';

import { trackConversationSendMessage, trackConversationStarted, trackTutorialEnded, trackRecordingTurnFailed, trackTurnStreamFallback } from '../utilities/tracking'
import * as Sentry from '@sentry/react';
import toast from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/dark.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faGamepad, faVolumeHigh } from '@fortawesome/free-solid-svg-icons';

import ActivityList from './Activities/ActivityList';
import ActivitySessionProgressBar from './Activities/ActivitySessionProgressBar';
import ModalGeneric from "./Modal/ModalGeneric";
import MicrophonePermissionModal from './Modal/MicrophonePermissionModal';
import Confetti from 'react-confetti';

// The /upload round-trip is bounded server-side by Netlify's synchronous function
// ceiling, so anything past this is a stalled connection rather than slow processing.
// Axios defaults to no timeout in the browser, and a request that never settles leaves
// `loading` latched on and the microphone disabled forever (MAD-788).
const UPLOAD_TIMEOUT_MS = 45000;

// A new turn's history payload must include the previous turn's committed messages,
// so the upload effect awaits the in-flight turn first — but bounded, so a hung
// stream can never wedge the microphone into waiting forever.
const TURN_SERIALIZATION_TIMEOUT_MS = 15000;

const ConversationWT = () => {
  const [showCompletionModal, setShowCompletionModal] = useState(false); // State for Completion Modal

  const { sharedState, setSharedState, switchToActivitiesView, updateSelectedActivity, getLatestLevel, setConversationStartHandler } = useSharedState();
  const plannerContext = usePlanner();
  const { currentPlannerData, checkPlannerDataOrPromptGoal } = plannerContext || {};
  const [, setIsPlannerLoading] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [pendingActivity, setPendingActivity] = useState(null);
  const [activityDocAtStart, setActivityDocAtStart] = useState(null);

  const { startActivityTimer } = useTimer(); // Get startTimer from TimerContext
  const { endConversation } = useEndConversation(); // Destructure the endConversation function
  const { width, height } = useWindowSize(); // Get window dimensions
  const chatHistoryRef = useRef([]);
  // The in-flight turn's promise (streamed or legacy). The lastRecordedBlob effect
  // awaits it (bounded) before starting the next turn so history stays complete.
  const activeTurnPromiseRef = useRef(Promise.resolve());

  const { t } = useTranslation();

  const BASE_API_URL = _config.url;
  const HTTP_upload = `${BASE_API_URL}upload`;

  const getSelectedActivityValue = useCallback(() => (
    sharedState.selectedActivity || localStorage.getItem('selectedActivity')
  ), [sharedState.selectedActivity]);

  const applyConversationProgress = useCallback((value) => {
    const activity = sharedState.selectedActivity || localStorage.getItem('selectedActivity');
    if (value && !isLearningActivity(activity)) return;
    setSharedState(prev => ({
      ...prev,
      conversationProgress: value
        ? mergeConversationProgress(prev.conversationProgress, value)
        : null,
    }));
  }, [setSharedState, sharedState.selectedActivity]);

  const readActivityDocAtStart = useCallback(async (selectedActivity) => {
    setActivityDocAtStart(null);
    const activityMatch = typeof selectedActivity === 'string'
      ? selectedActivity.match(/^learning-path-(\d+)$/)
      : null;
    const plannerId = currentPlannerData?.plannerId;
    if (!activityMatch || !plannerId) return null;

    const docData = await getLearningActivityDoc(`${plannerId}_${activityMatch[1]}`);
    setActivityDocAtStart(docData);
    return docData;
  }, [currentPlannerData?.plannerId]);

  const {
    attemptProgressIndex,
    startAttempt,
    reportChatLlmProgress,
    reportCompletion,
  } = useLiveActivityProgress(getSelectedActivityValue, applyConversationProgress);

  useEffect(() => {
    chatHistoryRef.current = sharedState.chatHistory || [];
  }, [sharedState.chatHistory]);


  /**
   * The activity signalled it is over (placement test, tutorial, exam practice).
   *
   * Showing the completion modal is not enough: `/end` is what dispatches the analysis
   * Cloud Function, and that function is what writes the analysis to the conversation,
   * records the placement-test result and level, and sends the learner their results
   * email. Until this called endConversation, a placement test finished inside the app
   * left no trace — so `fetchLastPlacementTestResult()` found nothing and the app asked
   * the same learner to take the test again, session after session. Over 60 days only
   * 45% of in-app tests were ever analysed, against 94% on the activity pages, which
   * have always ended themselves via `onTestCompleted`.
   *
   * `resetState: false` keeps the transcript on screen behind the modal; the modal's
   * "Go to activities" button still does the view switch. The history is passed
   * explicitly from the ref because `sharedState.chatHistory` has not re-rendered yet
   * when a turn completes, and /end would otherwise analyse a transcript missing its
   * final exchange.
   */
  const finishCompletedActivity = useCallback(() => {
    setShowCompletionModal(true);
    endConversation({ resetState: false, conversation: chatHistoryRef.current });
  }, [endConversation]);

  // Updates the chat history with new messages, including audio messages.
  // It logs the audio URL of the assistant's response
  // and sets the state for automatic playback of the next message.
  // It also places the audio URL in the chat history adn that is used by the ConversationComponent
  // to play the audio.
  // `options.streamed` marks a turn that arrived via /turn/stream: the assistant
  // message is stamped `streamed: true` (ConversationComponent skips the audio-gate
  // and WordReveal for it) and autoPlayNextMessage stays false — the audio already
  // played through the Web Audio queue, so flagging autoplay would double-play the
  // turn through the <audio> element.
  const updateChatHistory = useCallback((conversationArray, assistantMessage, audioUrl, userAudioUrl, options = {}) => {
    console.log('Updating chat history with audio:', { conversationArray, assistantMessage, audioUrl });

    const rawMessages = Array.isArray(conversationArray)
      ? conversationArray
      : [
        ...(conversationArray ? [{ role: "user", content: conversationArray, audio: userAudioUrl }] : []),
        ...(assistantMessage ? [{ role: "assistant", content: assistantMessage, audio: audioUrl }] : [])
      ];

    const messagesToAdd = rawMessages.map(msg => ({
      ...msg,
      content: stripSignals(msg.content),
      ...(options.streamed && msg.role === 'assistant' ? { streamed: true } : {}),
    }));

    if (messagesToAdd.length > 0) {
      const nextChatHistory = [...chatHistoryRef.current, ...messagesToAdd];
      chatHistoryRef.current = nextChatHistory;
      setSharedState(prev => ({
        ...prev,
        chatHistory: nextChatHistory,
        autoPlayNextMessage: !options.streamed
      }));
    }

    // Completion is handled after the history update so the closing exchange is already
    // in chatHistoryRef when finishCompletedActivity hands the transcript to /end.
    try {
      const parsedMessage = JSON.parse(assistantMessage);
      if (isCompletionResponse(parsedMessage)) {
        reportCompletion();
        if (sharedState.activityPageMode) {
          // In activity mode, notify via callback instead of showing modal
          if (sharedState.onTestCompleted) {
            sharedState.onTestCompleted();
          }
        } else {
          finishCompletedActivity();
        }
      }
    } catch (e) {
      // Not a JSON string, no action needed
    }

    trackConversationSendMessage(conversationArray, assistantMessage);
  }, [
    reportCompletion,
    finishCompletedActivity,
    setSharedState,
    sharedState.activityPageMode,
    sharedState.onTestCompleted
  ]);

  const updateLastReminderTime = (newLastReminderTime) => {
    setSharedState(prevState => ({
      ...prevState,
      lastReminderTime: newLastReminderTime
    }));
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Call endConversation when the tab is closed
      console.log("Tab closed")
      endConversation({ resetState: false, viaUnload: true });
      // Optionally, you can set a returnValue to show a confirmation dialog
      // event.returnValue = 'Are you sure you want to leave?';
      // Set a returnValue to show a confirmation dialog
      const confirmationMessage = t("end_conversation");
      event.returnValue = confirmationMessage; // Standard way to trigger a confirmation dialog
      return confirmationMessage; // Some browsers require this return statement
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [endConversation, t]);

  // TODO -- This should be put into a common helper
  function sanitizeData(data) {
    return Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== undefined)
    );
  }

  // Build conversation data object from current state (used for creating Firestore doc)
  const buildConversationData = (selectedActivity) => {
    return sanitizeData({
      userLevel: sharedState.userLevel ?? 3,
      userAge: sharedState.userAge || '10',
      userLanguage: sharedState.userLanguage || 'en',
      selectedActivity: selectedActivity,
      targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
      capturedFirstName: localStorage.getItem('capturedFirstName'),
      consentToFollowUp: localStorage.getItem('consentToFollowUp'),
      capturedEmail: localStorage.getItem('capturedEmail')
    });
  };

  // Microphone permission check
  const checkMicrophonePermission = async (activity) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: sharedState.echoCancellation,
          noiseSuppression: sharedState.noiseSuppression,
          autoGainControl: sharedState.autoGainControl
        }
      });
      stream.getTracks().forEach(track => track.stop());
      // Permission granted, proceed directly
      proceedWithStart(activity);
    } catch (error) {
      console.error('Microphone permission check failed:', error);
      // Permission denied or error, show the modal
      setPendingActivity(activity);
      setShowPermissionModal(true);
    }
  };

  // Proceed with start after permission check
  const proceedWithStart = async (activity) => {
    if (activity !== sharedState.selectedActivity) {
      updateSelectedActivity(activity, false);
    }

    let selectedActivity = activity ? activity : sharedState.selectedActivity;
    if (typeof selectedActivity === 'object' && selectedActivity && selectedActivity.type) {
      selectedActivity = sharedState.selectedActivity;
    }

    const consentToFollowUp = localStorage.getItem('consentToFollowUp');
    trackConversationStarted(false, selectedActivity, consentToFollowUp);

    const conversationData = buildConversationData(selectedActivity);

    // OPTIMISTIC UI: Set UI state immediately so conversation layout renders instantly
    // The mic button will show loading state until Firestore doc creation completes
    setSharedState(prevState => ({
      ...prevState,
      startTime: Date.now(),
      lastReminderTime: Date.now(),
      currentView: 'conversation',
      showStartButton: false,
      firstUserInput: true, // Render conversation layout immediately
      loading: true // Show loading state on mic button until doc created
    }));

    // Run Firestore write in background (don't await - optimistic UI)
    createConversationDoc(conversationData)
      .then(async () => {
        console.log('Conversation document created successfully');
        setSharedState(prevState => ({
          ...prevState,
          loading: false
        }));

        // One historical read per activity session, before the new attempt is
        // recorded. Current highlights remain local until conversation end.
        await readActivityDocAtStart(selectedActivity);

        // Begin a fresh attempt: bar at 0, start event recorded server-side
        startAttempt(selectedActivity);

        const time = getActivityDuration(selectedActivity);
        console.log(`Time ${time} for activity ${selectedActivity}`);
        if (time > 0) {
          startActivityTimer(time, () => {
            console.log("Timer has ended");
          });
        }
      })
      .catch((error) => {
        console.error('Failed to create conversation document:', error);
        toast(t('connection_error') || 'Connection error. Please try again.');
        setSharedState(prevState => ({
          ...prevState,
          loading: false
        }));
      });
  };

  // Checks permissions first
  const handleStart = useCallback(async (activity) => {
    console.log("handleStart", activity);

    // Safety check: if no activity and no planner data, prompt for learning goal or start free-form
    if (!activity && (!currentPlannerData || currentPlannerData.number_activities === 0)) {
      if (checkPlannerDataOrPromptGoal) {
        setIsPlannerLoading(true);
        const hasPlannerData = await checkPlannerDataOrPromptGoal();
        setIsPlannerLoading(false);

        if (hasPlannerData) {
          // User created a learning goal - get the first activity
          activity = getMainStartActivity(null, currentPlannerData);
        } else {
          // User declined to create a learning goal - start free-form conversation
          activity = null;
        }
      } else {
        // No planner context available (lite mode) - start free-form conversation
        activity = null;
      }
    }

    await checkMicrophonePermission(activity);
  }, [checkMicrophonePermission, currentPlannerData, checkPlannerDataOrPromptGoal]);


  // Register this component's conversation start handler
  useEffect(() => {
    // console.log('[ConversationWT] Registering conversation start handler', handleStart)
    setConversationStartHandler(handleStart)
    return () => {
      // console.log('[ConversationWT] Unregistering conversation start handler')
      setConversationStartHandler(null)
    }
  }, [setConversationStartHandler, handleStart])

  // New handlers for the permission modal
  const handlePermissionGranted = () => {
    setShowPermissionModal(false);
    if (pendingActivity) {
      proceedWithStart(pendingActivity);
      setPendingActivity(null);
    }
  };

  const handlePermissionDenied = () => {
    setShowPermissionModal(false);
    setPendingActivity(null);
    toast(t('microphone_permission_required') || 'Microphone permission is required to start the conversation');
  };

  /**
   * Hands the microphone back to the learner after a recorded turn fails to produce a reply.
   *
   * `loading` is a one-way latch: Recording.jsx sets it the instant the learner presses stop
   * and the mic button stays disabled until something clears it. Every path that abandons an
   * upload must funnel through here — a bare `return` leaves the learner staring at a dead
   * microphone with no way forward but a page reload, which is how MAD-788 stranded a learner
   * mid-placement-test five times over.
   */
  const releaseRecordingUI = useCallback((reason, message, details = {}) => {
    setSharedState(prevState => ({
      ...prevState,
      loading: false,
      record: false,
    }));

    toast(message);

    const activityId = getSelectedActivityValue();
    trackRecordingTurnFailed(activityId, reason, details);
    Sentry.addBreadcrumb({
      category: 'recording',
      level: 'warning',
      message: `Recorded turn released without a reply: ${reason}`,
      data: { activityId, conversationId: getFirestoreDocId(), ...details },
    });
  }, [setSharedState, getSelectedActivityValue]);

  /**
   * Binds a fetched audio URL to the assistant turn it belongs to. Matching by content
   * (not "last assistant message without audio") keeps the audio on the right turn even
   * if a newer reply landed while TTS was in flight. Tolerates a null audioUrl by
   * attaching nothing. `autoPlay` is legacy-only: the streaming path has already played
   * the audio, so flagging autoPlayNextMessage there would double-play the turn through
   * ConversationComponent's <audio> element.
   */
  const attachAudioToMessage = useCallback((assistantText, audioUrl, { autoPlay }) => {
    if (!audioUrl) return;
    setSharedState(prev => {
      const history = [...prev.chatHistory];
      for (let i = history.length - 1; i >= 0; i--) {
        if (history[i].role === 'assistant' && history[i].content === assistantText && !history[i].audio) {
          history[i] = { ...history[i], audio: audioUrl };
          break;
        }
      }
      return autoPlay
        ? { ...prev, chatHistory: history, autoPlayNextMessage: true }
        : { ...prev, chatHistory: history };
    });
  }, [setSharedState]);

  // The microphone stays locked from recording-stop until the reply's AUDIO is ready
  // (attached or begins streaming). Releasing earlier reopened a race: the learner
  // could start a new recording during the TTS wait, and the reply would then
  // auto-play straight into the open mic. Every terminal path of a turn must call
  // this exactly once — the 60s stuck-recording watchdog is the backstop for any
  // path that somehow doesn't.
  const releaseMicForReply = useCallback(() => {
    setSharedState(prev => ({ ...prev, loading: false, record: false }));
  }, [setSharedState]);

  // Fetch TTS audio after text-first response and attach it to the specific assistant message.
  // Also serves as the streamed-turn backfill: when /turn/stream reported ttsFailed (or
  // persisted no replay URL) and nothing played, this speaks the turn and attaches the URL.
  // Releases the mic on BOTH outcomes: the audio is ready (attach) or it never will be (catch).
  const fetchAndAttachAudio = useCallback(async (assistantText, traceId) => {
    try {
      const ttsResponse = await axios.post(`${BASE_API_URL}tts`, {
        text: assistantText,
        targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
        userLanguage: sharedState.userLanguage || 'en',
        speakingRate: parseFloat(sharedState.ttsSpeed) || 1.0,
        useSSML: true,
        ...(traceId && { traceId }),
      }, {
        // /tts is platform-capped at 26s server-side; without a client timeout a hung
        // request would keep the mic locked until the stuck watchdog fires at 60s.
        timeout: UPLOAD_TIMEOUT_MS,
      });
      attachAudioToMessage(assistantText, ttsResponse.data.audioUrl, { autoPlay: true });
    } catch (err) {
      console.error('[Text-first] TTS fetch failed:', err.message);
    } finally {
      releaseMicForReply();
    }
  }, [BASE_API_URL, sharedState.targetLanguage, sharedState.userLanguage, sharedState.ttsSpeed, attachAudioToMessage, releaseMicForReply]);

  // Streaming sibling of fetchAndAttachAudio: plays PCM chunks through the Web Audio
  // queue as they arrive instead of waiting for one MP3 URL. The avatar is driven
  // directly here (talking on first audible chunk, off when the queue drains) since
  // no <audio> element is involved. The audioUrl the server persisted is attached
  // afterwards so the turn stays replayable, but without autoPlay — it already played.
  const fetchAndAttachAudioStreaming = useCallback(async (assistantText) => {
    // Both proxy endpoints require a Firebase ID token in production (enforced
    // from the first prod deploy — no tokenless callers ever existed there).
    // Null is tolerated: dev may run without enforcement, and a 401 lands in
    // the pre-audio fallback like any other failure.
    const idToken = await getIdTokenOrNull();
    const { audioUrl, playedAudio } = await streamTTSAudio({
      url: `${_config.wsProxyUrl}/tts/stream`,
      idToken,
      payload: {
        text: assistantText,
        targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
        userLanguage: sharedState.userLanguage || 'en',
        speakingRate: parseFloat(sharedState.ttsSpeed) || 1.0,
      },
      callbacks: {
        // Audio is now audible — this is the moment the mic becomes safe to reopen.
        onFirstAudio: () => setSharedState(prev => ({ ...prev, talking: true, loading: false, record: false })),
        onPlaybackEnded: () => setSharedState(prev => ({ ...prev, talking: false })),
      },
    });
    attachAudioToMessage(assistantText, audioUrl, { autoPlay: false });
    // Stream completed without a single audio event (e.g. empty synthesis): no
    // audio is coming, so release rather than leave the mic latched.
    if (!playedAudio) releaseMicForReply();
  }, [sharedState.targetLanguage, sharedState.userLanguage, sharedState.ttsSpeed, attachAudioToMessage, setSharedState, releaseMicForReply]);

  // Picks the TTS path per the feature flag. Streaming failures before any audio fall
  // back to the legacy path (identical behavior to today); failures mid-playback stop
  // cleanly instead — replaying the utterance from the start after the learner already
  // heard part of it is worse than silence.
  const fetchAndAttachAudioForTurn = useCallback(async (assistantText, traceId) => {
    if (!isTTSStreamingEnabled()) {
      return fetchAndAttachAudio(assistantText, traceId);
    }
    try {
      await fetchAndAttachAudioStreaming(assistantText);
    } catch (error) {
      if (error?.playedAudio) {
        console.warn('[TTS stream] Failed mid-playback, stopping without fallback:', error.message);
        stopStreamingPlayback();
        setSharedState(prev => ({ ...prev, talking: false }));
        return;
      }
      // A deliberate stop (activity switch, recording watchdog) aborted the stream before
      // any audio: no fallback — its audio would autoplay after the learner moved on.
      if (error?.name === 'AbortError') {
        console.warn('[TTS stream] Aborted before audio, not falling back');
        releaseMicForReply();
        return;
      }
      console.warn('[TTS stream] Failed before audio, falling back to legacy /tts:', error.message);
      // Bumps the playback generation: any MP3 decode still in flight from the dead
      // stream is invalidated and can never schedule over the fallback's audio.
      stopStreamingPlayback();
      await fetchAndAttachAudio(assistantText, traceId);
    }
  }, [fetchAndAttachAudio, fetchAndAttachAudioStreaming, setSharedState, releaseMicForReply]);

  /**
   * Which model should serve this turn: 'conversaflex' (our fine-tune) or
   * 'default' (stock Gemini). Driven by the "Audio model" setting in the
   * "..." menu, with ?chat_model= as a per-session override for testing.
   *
   * Returns null when the learner never chose, and the field is then left out
   * of the request entirely — an explicit value forces that model in both
   * directions, while its absence lets the server-side UNIFIED_AUDIO_MODEL
   * default decide.
   *
   * Streamed turns only. The legacy /upload path is the generic fallback and
   * always runs on stock Gemini, whatever is selected here.
   */
  const getUnifiedChatModel = useCallback(
    () => getUrlParameter('chat_model') || sharedState.audioModel || null,
    [sharedState.audioModel],
  );

  const uploadAudio = useCallback(async (audioBlob, { streamFallbackReason } = {}) => {
    console.log('🚨 UPLOAD AUDIO FUNCTION CALLED!');

    // Every rejection here must hand the microphone back — see recordingGuards.js.
    const verdict = classifyRecordedBlob(audioBlob);
    if (!verdict.ok) {
      console.warn('Recorded clip rejected before upload:', verdict.reason, verdict.details);
      releaseRecordingUI(
        verdict.reason,
        t(verdict.messageKey) || RECORDING_GUARD_FALLBACK_MESSAGES[verdict.messageKey],
        verdict.details
      );
      return;
    }

    const currentLanguageVariant = sharedState.languageVariant || localStorage.getItem('languageVariant') || 'france';

    // Retry creating conversation doc if it failed during optimistic UI start
    if (!getFirestoreDocId()) {
      console.log('No firestoreDocId - retrying conversation doc creation before upload');
      try {
        await createConversationDoc(buildConversationData(sharedState.selectedActivity));
      } catch (error) {
        console.error('Failed to create conversation doc on retry:', error);
        releaseRecordingUI(
          'conversation_doc_failed',
          t('connection_error') || 'Unable to start conversation. Please try again.',
          { errorMessage: error?.message }
        );
        return;
      }
    }

    const formData = new FormData();
    formData.append("audioFile", audioBlob, "audio.mp3");

    // Strip audio URLs from chat history before sending - they're not used by the AI
    // and add significant size to the request payload (signed URLs are ~500+ chars each)
    const chatHistoryWithoutAudio = (sharedState.chatHistory || []).map(({ role, content }) => ({ role, content }));
    const chatData = JSON.stringify(chatHistoryWithoutAudio);
    formData.append("conversation", chatData);
    formData.append("firestoreDocId", getFirestoreDocId());
    formData.append("name", getCurrentUserName() || '');
    formData.append("level", getLatestLevel() ?? 3); // Default to 3 if user has no level set
    formData.append("age", sharedState.childrenMode ? '10' : (sharedState.userAge || '20'));
    formData.append("userLanguage", sharedState.userLanguage || 'en');
    formData.append("targetLanguage", sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr');
    formData.append("lastReminderTime", sharedState.lastReminderTime);
    formData.append("userId", getCurrentUserId());
    formData.append("languageVariant", currentLanguageVariant);
    formData.append("ttsSpeed", sharedState.ttsSpeed || 1.0);
    formData.append("preferredPronoun", sharedState.preferredPronoun || 'both');
    // Why the streamed turn fell back here. Logged server-side: networks that block
    // our client telemetry still reach /upload.
    if (streamFallbackReason) {
      formData.append("streamFallbackReason", streamFallbackReason);
    }

    if (getUserMemory()) {
      formData.append("memory", getUserMemory() || "");
    }

    formData.append("activity", sharedState.selectedActivity);

    // Pass activity title from planner data for tracking (avoids extra Firebase fetch on backend)
    if (sharedState.selectedActivity?.startsWith('learning-path-') && currentPlannerData) {
      const activityNum = sharedState.selectedActivity.match(/learning-path-(\d+)/)?.[1];
      const activityData = activityNum && currentPlannerData[activityNum];
      if (activityData?.title) {
        formData.append("activityTitle", activityData.title);
      }
    }

    // Add organizationId if available (performance optimization for multi-org users)
    const currentOrganizationId = getCurrentOrganizationId();
    if (currentOrganizationId) {
      formData.append("organizationId", currentOrganizationId);
      console.log("Including organizationId in upload:", currentOrganizationId);
    }
    const currentOrganizationName = getCurrentOrganizationName();
    if (currentOrganizationName) {
      formData.append("organizationName", currentOrganizationName);
    }

    try {
      const response = await postUploadWithRetry(axios, `${HTTP_upload}?text_first=true`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: UPLOAD_TIMEOUT_MS,
      });

      console.log('Upload successful', response);
      console.log('Response data:', JSON.stringify(response.data, null, 2));
      const responseProgressIndex = getConversationProgressIndex(response.data.conversationProgress);
      if (response.data.conversationProgress) {
        applyConversationProgress(response.data.conversationProgress);
      }

      if (response.data.didNotCatch) {
        // The clip couldn't be transcribed by any provider. The server sends only
        // a signal; the re-prompt text lives here so it's localized to the language
        // being learned, then spoken via the same /tts path as any other turn.
        const repromptText = getTargetLanguageText(
          sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
          'didntCatch'
        );
        updateChatHistory(null, repromptText, null, null);
        // Mic stays locked until the reprompt's audio is ready (or fails) — released
        // inside the fetch below, same contract as a normal reply.
        fetchAndAttachAudioForTurn(repromptText, response.data.traceId);
      } else if (response.data.text && response.data.textFirst) {
        // Text-first mode: show text immediately, fetch audio in background
        newAudioResponse(response.data.text, null);

        if (response.data.conversation) {
          console.log('Adding new messages to chat history (text-first)');
          updateChatHistory(
            response.data.conversation,
            response.data.text,
            null,
            response.data.userAudio
          );
        } else {
          const fallbackText = `⚠️ ${t('fallback_no_audio')}`;
          updateChatHistory(fallbackText, response.data.text, null, response.data.userAudio);
        }

        if (response.data.completionMessage) {
          console.log('Function called ' + response.data.completionMessage);
          reportCompletion();
          if (sharedState.activityPageMode && sharedState.onTestCompleted) {
            sharedState.onTestCompleted();
          } else {
            finishCompletedActivity();
          }
          trackTutorialEnded();
        } else if (responseProgressIndex !== null) {
          // Per-turn progress reported by the chat LLM
          reportChatLlmProgress(responseProgressIndex);
        }

        if (response.data.newLastReminderTime) {
          updateLastReminderTime(response.data.newLastReminderTime);
        }

        // The mic is deliberately NOT released here: the reply's audio hasn't arrived,
        // and an early release let the learner start recording right before autoplay
        // fired — Madame AI's voice went straight into the open mic. The fetch below
        // releases when audio is ready (attached / first streamed chunk) or on failure;
        // meanwhile the message shows its text with a loading play-button.
        fetchAndAttachAudioForTurn(response.data.text, response.data.traceId);

      } else if (response.data.text && response.data.audio) {
        // Classic mode: text + audio arrive together
        newAudioResponse(response.data.text, response.data.audio);

        if (response.data.conversation) {
          console.log('Adding new messages to chat history')
          updateChatHistory(
            response.data.conversation,
            response.data.text,
            response.data.audio,
            response.data.userAudio
          );
        } else {
          const fallbackText = `⚠️ ${t('fallback_no_audio')}`
          updateChatHistory(fallbackText, response.data.text, response.data.audio, response.data.userAudio)
        }

        if (response.data.completionMessage) {
          console.log("Function called " + response.data.completionMessage)
          reportCompletion();
          if (sharedState.activityPageMode && sharedState.onTestCompleted) {
            sharedState.onTestCompleted();
          } else {
            finishCompletedActivity();
          }
          trackTutorialEnded();
        } else if (responseProgressIndex !== null) {
          // Per-turn progress reported by the chat LLM
          reportChatLlmProgress(responseProgressIndex);
        }

        if (response.data.newLastReminderTime) {
          console.log("Updating reminder time " + response.data.newLastReminderTime);
          updateLastReminderTime(response.data.newLastReminderTime);
        }
      } else {
        // Model didn't respond properly - handle as error
        console.error('Model did not respond with valid data', response);

        releaseRecordingUI(
          'empty_model_response',
          t('fallback_no_audio') || 'The AI did not respond. Please try again.'
        );
        return;
      }
    } catch (error) {
      console.error('Upload error', error);

      // A stalled request aborted by our own timeout is a connection problem, not an
      // application error. Surfacing axios' raw "timeout of 45000ms exceeded" would be
      // useless to the learner, so it gets its own localized message.
      const isTimeout = error?.code === 'ECONNABORTED';

      // Build user-friendly error message with safe null checks
      let errorMessage = t('fallback_no_audio') || 'An error occurred. Please try again.';

      // Safely extract error details
      if (isTimeout) {
        errorMessage = t('recording_timeout') || 'The connection timed out. Please try recording again.';
      } else if (error) {
        if (error.response?.data?.message) {
          // Server returned error message
          errorMessage = error.response.data.message;
        } else if (error.message) {
          // JavaScript error message
          errorMessage = error.message;
        }

        // Special handling for audio decode errors
        if (errorMessage.includes('could not be decoded')) {
          errorMessage = t('audio_decode_error') || 'Audio file could not be processed. Please try recording again.';
        }
      }

      // Resets loading/record so the learner can retry, and toasts the message above.
      releaseRecordingUI(
        isTimeout ? 'upload_timeout' : 'upload_failed',
        errorMessage,
        { errorCode: error?.code, statusCode: error?.response?.status }
      );

      Sentry.captureException(error, {
        tags: {
          function: 'uploadAudio',
          activityId: getSelectedActivityValue(),
          reason: isTimeout ? 'upload_timeout' : 'upload_failed',
        },
        extra: {
          conversationId: getFirestoreDocId(),
          errorCode: error?.code,
          statusCode: error?.response?.status,
          uploadRetryCount: error?.uploadRetryCount,
        },
      });
    }
  }, [
    HTTP_upload,
    applyConversationProgress,
    sharedState.userAge,
    sharedState.userLanguage,
    sharedState.selectedActivity,
    updateChatHistory,
    sharedState.languageVariant,
    updateLastReminderTime,
    releaseRecordingUI,
    attachAudioToMessage,
    fetchAndAttachAudioForTurn,
    Date.now() ]);

  /**
   * Streamed sibling of uploadAudio: one POST to the Cloud Run proxy's /turn/stream
   * returns the transcript, reply deltas and PCM audio as NDJSON events.
   *
   * Live text renders through the ephemeral `sharedState.streamingTurn` bubble —
   * NEVER through chatHistory, which is persisted wholesale (localStorage + Firestore)
   * on every change, so per-delta writes there would persist every partial string.
   * The turn commits exactly once, at the server's terminal done event, through the
   * same updateChatHistory as every other path.
   *
   * Throws (TurnStreamError or a stamped foreign error, always carrying `playedAudio`
   * plus the partial text seen so far) — uploadAudioStreamedWithFallback decides
   * between partial-commit and wholesale legacy re-run.
   */
  const uploadAudioStreamed = useCallback(async (audioBlob) => {
    console.log('🚨 UPLOAD AUDIO STREAMED FUNCTION CALLED!');

    // Same pre-flight guards as uploadAudio: every abandoned upload must hand the
    // microphone back — see releaseRecordingUI (MAD-788).
    const verdict = classifyRecordedBlob(audioBlob);
    if (!verdict.ok) {
      console.warn('Recorded clip rejected before streamed upload:', verdict.reason, verdict.details);
      releaseRecordingUI(
        verdict.reason,
        t(verdict.messageKey) || RECORDING_GUARD_FALLBACK_MESSAGES[verdict.messageKey],
        verdict.details
      );
      return;
    }

    // Retry creating conversation doc if it failed during optimistic UI start.
    // A failure here is a connection problem the legacy path would hit identically,
    // so release the microphone instead of falling back.
    if (!getFirestoreDocId()) {
      console.log('No firestoreDocId - retrying conversation doc creation before streamed upload');
      try {
        await createConversationDoc(buildConversationData(sharedState.selectedActivity));
      } catch (error) {
        console.error('Failed to create conversation doc on retry:', error);
        releaseRecordingUI(
          'conversation_doc_failed',
          t('connection_error') || 'Unable to start conversation. Please try again.',
          { errorMessage: error?.message }
        );
        return;
      }
    }

    // Ephemeral live-turn state: ConversationComponent renders these as live bubbles.
    setSharedState(prev => ({
      ...prev,
      streamingTurn: { userText: null, assistantText: '', status: 'waiting' },
    }));

    // Mirrors of the streamed state kept in plain variables: the fallback wrapper
    // needs them at error time, and reading React state there would race the queue.
    let committed = false;
    let liveTranscript = null;
    let liveAssistantText = '';

    const commitStreamedTurn = ({ transcript, fullText, audioUrl, userAudio }) => {
      if (committed || !fullText) return false;
      committed = true;
      updateChatHistory(
        transcript || `⚠️ ${t('fallback_no_audio')}`,
        fullText,
        audioUrl != null ? audioUrl : null,
        userAudio != null ? userAudio : null,
        { streamed: true }
      );
      // Same tick as the commit above: React 18 batches both into one render, so the
      // live bubble is replaced by the committed message without a flicker. NOTE:
      // no mic release here — that belongs to the audio lifecycle (onFirstAudio, the
      // no-audio backfill, or the explicit no-audio release after the stream).
      setSharedState(prev => ({ ...prev, streamingTurn: null }));
      return true;
    };

    try {
      const idToken = await getIdTokenOrNull();
      if (!idToken) {
        // Pre-audio by construction: nothing was sent yet, the wrapper falls back.
        throw new TurnStreamError('No Firebase ID token available for the turn stream', { reason: 'missing_id_token' });
      }

      const audioBase64 = await blobToBase64(audioBlob);
      const currentLanguageVariant = sharedState.languageVariant || localStorage.getItem('languageVariant') || 'france';

      const streamedChatModel = getUnifiedChatModel();
      const payload = {
        audioBase64,
        mimeType: audioBlob.type || 'audio/webm',
        // Last 20 turns, stripped to {role, content}: audio URLs are useless to the
        // model and signed URLs add ~500 chars each. Read from the ref so a turn
        // committed moments ago (turn serialization) is always included.
        conversation: (chatHistoryRef.current || []).slice(-20).map(({ role, content }) => ({ role, content })),
        firestoreDocId: getFirestoreDocId(),
        name: getCurrentUserName() || '',
        level: getLatestLevel() ?? 3, // Default to 3 if user has no level set
        age: sharedState.childrenMode ? '10' : (sharedState.userAge || '20'),
        userLanguage: sharedState.userLanguage || 'en',
        targetLanguage: sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
        lastReminderTime: sharedState.lastReminderTime,
        languageVariant: currentLanguageVariant,
        ttsSpeed: sharedState.ttsSpeed || 1.0,
        preferredPronoun: sharedState.preferredPronoun || 'both',
        ...(getUserMemory() ? { memory: getUserMemory() } : {}),
        activity: sharedState.selectedActivity,
        // No userId (the server takes it from the verified token), no
        // instructions (prompts are generated server-side).
        // Which model serves the turn: our Conversaflex fine-tune or the
        // stock one. Sent only when the learner made an explicit choice, so
        // an untouched client still follows the server-side default. Same
        // field, same precedence, as the legacy /upload path below.
        ...(streamedChatModel ? { chatModel: streamedChatModel } : {}),
      };

      // Pass activity title from planner data for tracking (avoids extra Firebase fetch on backend)
      if (sharedState.selectedActivity?.startsWith('learning-path-') && currentPlannerData) {
        const activityNum = sharedState.selectedActivity.match(/learning-path-(\d+)/)?.[1];
        const activityData = activityNum && currentPlannerData[activityNum];
        if (activityData?.title) {
          payload.activityTitle = activityData.title;
        }
      }
      const currentOrganizationId = getCurrentOrganizationId();
      if (currentOrganizationId) {
        payload.organizationId = currentOrganizationId;
      }
      const currentOrganizationName = getCurrentOrganizationName();
      if (currentOrganizationName) {
        payload.organizationName = currentOrganizationName;
      }

      const result = await streamConversationTurn({
        url: `${_config.wsProxyUrl}/turn/stream`,
        payload,
        idToken,
        callbacks: {
          onTranscript: (text) => {
            liveTranscript = text || '';
            setSharedState(prev => ({
              ...prev,
              streamingTurn: {
                ...(prev.streamingTurn || { assistantText: '' }),
                userText: text,
                status: 'generating',
              },
            }));
          },
          // Deliberately NO mic release here: text is flowing but audio hasn't started,
          // and releasing now reopened the record-during-autoplay race. The release
          // happens at onFirstAudio (or a terminal no-audio path).
          onFirstDelta: () => {},
          onDelta: (text) => {
            if (!text) return;
            liveAssistantText += text;
            setSharedState(prev => (prev.streamingTurn
              ? {
                ...prev,
                streamingTurn: {
                  ...prev.streamingTurn,
                  assistantText: (prev.streamingTurn.assistantText || '') + text,
                },
              }
              : prev));
          },
          // Audio is audible — the mic is safe to reopen from this moment.
          onFirstAudio: () => setSharedState(prev => ({ ...prev, talking: true, loading: false, record: false })),
          onPlaybackEnded: () => setSharedState(prev => ({ ...prev, talking: false })),
          onDidNotCatch: () => {
            // The clip couldn't be transcribed by any provider. Same reprompt flow as
            // the legacy path: the text lives client-side so it's localized to the
            // language being learned, then spoken via the same TTS path as any turn.
            committed = true; // the reprompt IS this turn's outcome; done carries no fullText
            const repromptText = getTargetLanguageText(
              sharedState.targetLanguage || localStorage.getItem('targetLanguage') || 'fr',
              'didntCatch'
            );
            setSharedState(prev => ({ ...prev, streamingTurn: null }));
            updateChatHistory(null, repromptText, null, null);
            // Mic release happens inside the fetch, once the reprompt's audio is ready.
            fetchAndAttachAudioForTurn(repromptText);
          },
          onDone: (doneEvent) => {
            const didCommit = commitStreamedTurn(doneEvent);
            if (!didCommit) {
              // didNotCatch already handled the turn, or done carried no text — just
              // make sure the ephemeral bubble and the mic latch are cleared.
              setSharedState(prev => (prev.streamingTurn
                ? { ...prev, streamingTurn: null, loading: false, record: false }
                : prev));
            }
            // Post-commit block, mirroring the legacy text-first branch.
            if (doneEvent.completionMessage) {
              console.log('Function called ' + doneEvent.completionMessage);
              reportCompletion();
              if (sharedState.activityPageMode && sharedState.onTestCompleted) {
                sharedState.onTestCompleted();
              } else {
                finishCompletedActivity();
              }
              trackTutorialEnded();
            } else if (doneEvent.conversationProgress != null) {
              // Per-turn progress reported by the chat LLM. Learning-path turns
              // send {progressIndex, errors, acquiredConcepts} — the same shape
              // /upload returns — so the transcript highlighting and the
              // end-of-activity summary get their data here too; every other
              // activity sends the bare index.
              const streamedProgressIndex = getConversationProgressIndex(doneEvent.conversationProgress);
              applyConversationProgress(doneEvent.conversationProgress);
              if (Number.isFinite(streamedProgressIndex)) {
                reportChatLlmProgress(streamedProgressIndex);
              }
            }
            if (doneEvent.newLastReminderTime) {
              updateLastReminderTime(doneEvent.newLastReminderTime);
            }
          },
        },
      });

      // TTS died (or persisted no replay URL) and nothing was heard: backfill through
      // the legacy unary /tts so the turn still gets spoken — it also attaches the
      // replay URL to the committed message AND releases the mic when its audio is
      // ready (or fails). If no backfill applies and nothing ever played, release
      // here: no audio is coming for this turn.
      if (
        committed &&
        !result.playedAudio &&
        (result.ttsFailed || result.doneEvent.audioUrl == null) &&
        result.doneEvent.fullText
      ) {
        await fetchAndAttachAudio(result.doneEvent.fullText);
      } else if (!result.playedAudio) {
        releaseMicForReply();
      }
    } catch (error) {
      // Stamp the partial-turn context on the error so the fallback wrapper can
      // commit what the learner already saw/heard without reading React state.
      if (error && typeof error === 'object') {
        try {
          error.partialTranscript = liveTranscript;
          error.partialAssistantText = liveAssistantText;
          error.committed = committed;
        } catch (stampError) {
          // Frozen error object; the wrapper treats missing fields as "nothing to commit".
        }
      }
      throw error;
    }
  }, [
    getUnifiedChatModel,
    sharedState.selectedActivity,
    sharedState.languageVariant,
    sharedState.userAge,
    sharedState.childrenMode,
    sharedState.userLanguage,
    sharedState.targetLanguage,
    sharedState.lastReminderTime,
    sharedState.ttsSpeed,
    sharedState.preferredPronoun,
    sharedState.activityPageMode,
    sharedState.onTestCompleted,
    currentPlannerData,
    updateChatHistory,
    setSharedState,
    releaseRecordingUI,
    getLatestLevel,
    reportCompletion,
    reportChatLlmProgress,
    fetchAndAttachAudio,
    fetchAndAttachAudioForTurn,
    releaseMicForReply,
    t ]);

  /**
   * Fallback boundary for the streamed turn path — `playedAudio` splits the two
   * failure classes, same contract as the streamed TTS path:
   *
   * - Audio already reached the learner: re-running the whole turn would replay it
   *   from the start, so commit the partial text as the turn and stop cleanly.
   * - Nothing played yet: the learner saw at most a transient bubble. Clear it,
   *   re-latch `loading` (re-arms the stuck-recording watchdog), track the reason,
   *   and re-run the legacy upload wholesale.
   */
  const uploadAudioStreamedWithFallback = useCallback(async (audioBlob) => {
    try {
      await uploadAudioStreamed(audioBlob);
    } catch (error) {
      if (error?.playedAudio) {
        console.warn('[Turn stream] Failed mid-playback, committing partial turn:', error?.message);
        if (!error.committed && error.partialAssistantText) {
          updateChatHistory(
            error.partialTranscript || `⚠️ ${t('fallback_no_audio')}`,
            error.partialAssistantText,
            null,
            null,
            { streamed: true }
          );
        }
        stopStreamingPlayback();
        setSharedState(prev => ({
          ...prev,
          talking: false,
          streamingTurn: null,
          loading: false,
          record: false,
        }));
        return;
      }

      // A deliberate stop (activity switch, conversation end) aborts the fetch — not
      // a failure. Drop the turn: re-running an abandoned recording through the
      // legacy path is the last thing the learner wants.
      if (error?.name === 'AbortError') {
        console.warn('[Turn stream] Aborted before audio, dropping the turn');
        setSharedState(prev => ({ ...prev, streamingTurn: null, loading: false, record: false }));
        return;
      }

      const reason = error?.reason
        || (error?.name === 'TurnStreamError' ? 'stream_failed' : null)
        || (error?.name === 'SyntaxError' ? 'malformed_stream' : 'network_error');
      console.warn('[Turn stream] Failed before audio, falling back to legacy upload:', error?.message);
      trackTurnStreamFallback(getSelectedActivityValue(), reason, {
        errorMessage: error?.message,
        statusCode: error?.httpStatus,
      });
      // Bump the playback generation so any in-flight MP3 decode from the dead turn
      // stream is invalidated — it must never schedule over the legacy re-run's audio.
      stopStreamingPlayback();
      // Re-latch loading before the legacy re-run: uploadAudio expects the same
      // latched state a fresh recording leaves behind.
      setSharedState(prev => ({ ...prev, streamingTurn: null, loading: true }));
      await uploadAudio(audioBlob, { streamFallbackReason: reason });
    }
  }, [uploadAudioStreamed, uploadAudio, updateChatHistory, setSharedState, getSelectedActivityValue, t]);

  function newAudioResponse(text, audioUrl) {
    setSharedState(prevState => ({
      ...prevState,
      talking: false
    }));
  }

  const handleActivitySelect = (activity) => {
    console.log("handleActivitySelect", activity);
    handleStart(activity);
  };

  useEffect(() => {
    const limit = getUrlParameter('limit');

    setSharedState(prevState => ({
      ...prevState,
      timeLimit: limit ? parseInt(limit, 10) : null,
    }));
  }, [setSharedState]);

  useEffect(() => {
    if (!sharedState.lastRecordedBlob) return;

    // react-mic handed back something we can't upload. `loading` is already latched on
    // from onStop, so bailing out silently here would disable the mic permanently.
    if (!(sharedState.lastRecordedBlob.blob instanceof Blob)) {
      console.error('Recorded blob is not a Blob, releasing the microphone', sharedState.lastRecordedBlob);
      releaseRecordingUI(
        'malformed_recording',
        t('audio_decode_error') || 'Audio file could not be processed. Please try recording again.'
      );
      setSharedState(prevState => ({ ...prevState, lastRecordedBlob: null }));
      return;
    }

    (async () => {
      // Turn serialization: the next turn's history payload must include the previous
      // turn's committed messages, so wait (bounded) for the in-flight turn first.
      try {
        await Promise.race([
          activeTurnPromiseRef.current,
          new Promise(resolve => setTimeout(resolve, TURN_SERIALIZATION_TIMEOUT_MS)),
        ]);
      } catch (raceError) {
        // The previous turn's own handlers already dealt with its failure.
      }

      const useTurnStream = isTurnStreamingEnabled() && isTurnStreamEligible({
        activity: getSelectedActivityValue(),
        authUser: auth.currentUser,
      });

      const turnPromise = useTurnStream
        ? uploadAudioStreamedWithFallback(sharedState.lastRecordedBlob.blob)
        : uploadAudio(sharedState.lastRecordedBlob.blob);
      activeTurnPromiseRef.current = turnPromise;

      try {
        await turnPromise;
      } catch (turnError) {
        // Both paths handle their own failures; this only prevents an unhandled
        // rejection if a handler itself throws.
        console.error('Turn upload failed unexpectedly:', turnError);
      } finally {
        setSharedState(prevState => ({
          ...prevState,
          lastRecordedBlob: null,
        }));
      }
    })();
  }, [sharedState.lastRecordedBlob]); // Remove uploadAudio dependency to prevent infinite loop

  const switchToActivities = () => {
    setShowCompletionModal(false);
    switchToActivitiesView();
  }

  // Check URL parameter on component mount
  useEffect(() => {
    const showModalParam = getUrlParameter('showModal');
    if (showModalParam === 'true') {
      setTimeout(() => {
        setShowCompletionModal(true);
      }, 1000);
    }
  }, []);

  const filteredChatHistory = useMemo(
    () => (sharedState.chatHistory || []).filter(msg => msg.role !== 'system'),
    [sharedState.chatHistory]
  );
  const sessionProgressActivity = getSelectedActivityValue();
  const showSessionProgress = shouldShowSessionProgress(sessionProgressActivity);

  return (
    <div>
      {!sharedState.showStartButton &&
        <div className="text-center">
          <ActivityList visible={sharedState.currentView === 'activities'} onSelect={handleActivitySelect} />
        </div>
      }

      {!sharedState.firstUserInput && sharedState.showStartButton && sharedState.currentView !== 'activities' && !sharedState.activityPageMode &&
        <div className="text-center commencer">
          {(() => {
            return (
              <>
                {(() => {
                  const hasPlan = currentPlannerData && currentPlannerData.number_activities > 0;
                  const targetText = getTargetLanguageText(sharedState.targetLanguage, hasPlan ? 'continueYourLearningPlan' : 'startYourLearningPlan');
                  const nativeText = t(hasPlan ? 'continue_your_learning_plan' : 'start_your_learning_plan');
                  return (
                    <button
                      onClick={() => handleStart(getMainStartActivity(sharedState.selectedActivity, currentPlannerData))}
                      className="start-conversation"
                      aria-label={`${targetText} - ${nativeText}`}
                    >
                      {targetText}<br />
                      <span className="subtitle">{nativeText}</span>
                    </button>
                  );
                })()}
                {localStorage.getItem('onboardingV2Completed') !== 'true' && (
            <>
              <button
                className="button activities-button"
                onClick={() => handleStart(getFreeformStartActivity())}
                aria-label={t('activity_start_conversation')}
              >
                <FontAwesomeIcon icon={faComments} aria-hidden="true" /> {t('activity_start_conversation')}
              </button>
              <button
                className="button activities-button"
                onClick={switchToActivitiesView}
                aria-label={t('commencer_select_activity')}
              >
                <FontAwesomeIcon icon={faGamepad} aria-hidden="true" /> {t('commencer_select_activity')}
              </button>
                </>
                )}
                <p className="volume-warning" role="status" aria-live="polite">
                  <FontAwesomeIcon icon={faVolumeHigh} aria-hidden="true" /> {t('warning_volume')}
                </p>
                <p className="ai-disclaimer">{t('ai_can_make_mistakes')}</p>
              </>
            );
          })()}
        </div>
      }

      {sharedState.firstUserInput && sharedState.currentView === 'conversation' &&
        <div>
          {showSessionProgress && (
            <ActivitySessionProgressBar
              progressIndex={attemptProgressIndex ?? 0}
              selectedActivity={sessionProgressActivity}
              conversationProgress={sharedState.conversationProgress}
              activityDoc={activityDocAtStart}
            />
          )}
          <ConversationComponent
            chatHistory={filteredChatHistory}
            sharedState={sharedState}
            setSharedState={setSharedState}
          />
        </div>
      }

      {/* Microphone Permission Modal */}
      <MicrophonePermissionModal
        isOpen={showPermissionModal}
        onPermissionGranted={handlePermissionGranted}
        onPermissionDenied={handlePermissionDenied}
      />

      {/* Completion Modal - hidden in activity mode (results shown inline by ActivityApp) */}
      {showCompletionModal && !sharedState.activityPageMode && (
        <Confetti
          width={width}
          height={height}
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }}
        />
      )}
      {showCompletionModal && !sharedState.activityPageMode && (
        <ModalGeneric
          isOpen={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          title={t('tutorial_completed_title')}>
          {isLearningActivity(sessionProgressActivity) && (
            <ConversationProgressSummary conversationProgress={sharedState.conversationProgress} />
          )}
          <p>{t('tutorial_completed_message')}</p>
          <button className="modal-button" onClick={switchToActivities}>
            {t('go_to_activities')}
          </button>
        </ModalGeneric>
      )}
    </div>
  );
}

export default ConversationWT;
